/**
 * Tarjeta de un documento descargable. Si `url` es null se pinta
 * como "Pendiente de subir" (deshabilitado). Cuando la URL exista se
 * abre en pestaña nueva.
 */

import { Button, Card, Space, Tag, Typography } from "antd";
import {
  CloudDownloadOutlined,
  CloudUploadOutlined,
  FilePdfOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Link } from "react-router";
import { useGetIdentity } from "@refinedev/core";
import type { DocumentEntry } from "@/data/onboarding/types";
import type { UserInfo } from "@/types/auth";
import { ADMIN_USER_TYPES } from "@/types/auth";
import {
  resolveDocumentUrl,
  useOnboardingAssets,
} from "./OnboardingAssetsContext";

const { Text, Title } = Typography;

function formatSize(bytes: number | null): string | null {
  if (bytes == null) return null;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${Math.round(kb)} KB`;
}

interface Props {
  doc: DocumentEntry;
}

export const DocumentCard: React.FC<Props> = ({ doc }) => {
  // Prioridad: URL subida en BD (producción) sobre el fallback estático
  // del JSON. Tamaño idem — si la BD lo trae al día lo usamos.
  const { assetsBySlug } = useOnboardingAssets();
  const bd = assetsBySlug.get(doc.slug);
  const effectiveUrl = resolveDocumentUrl(doc.slug, doc.url, assetsBySlug);
  const effectiveSize = bd?.size_bytes ?? doc.sizeBytes;
  const isPending = !effectiveUrl;
  const sizeLabel = formatSize(effectiveSize);
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );

  return (
    <Card
      size="small"
      style={{ height: "100%" }}
      styles={{ body: { display: "flex", flexDirection: "column", gap: 12 } }}
    >
      <Space align="start" size={12}>
        <FilePdfOutlined style={{ fontSize: 28, color: isPending ? "#bfbfbf" : "#cf1322" }} />
        <div style={{ flex: 1 }}>
          <Title level={5} style={{ marginBottom: 4 }}>
            {doc.title}
          </Title>
          {doc.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {doc.description}
            </Text>
          ) : null}
        </div>
      </Space>

      <Space size={8} style={{ marginTop: "auto" }} wrap>
        {sizeLabel ? <Tag>{sizeLabel}</Tag> : null}
        {isPending ? (
          <>
            <Tag icon={<ClockCircleOutlined />} color="default">
              Pendiente de subir
            </Tag>
            {/* Sólo mostramos el botón "Subir" a los admins — los
                auditores/coord ven el tag pendiente pero no el CTA. */}
            {isAdmin ? (
              <Link to="/onboarding/admin/assets">
                <Button
                  size="small"
                  icon={<CloudUploadOutlined />}
                  type="dashed"
                >
                  Subir
                </Button>
              </Link>
            ) : null}
          </>
        ) : (
          <Button
            type="primary"
            icon={<CloudDownloadOutlined />}
            href={effectiveUrl ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            Descargar
          </Button>
        )}
      </Space>
    </Card>
  );
};
